import {create} from 'zustand';
import axios from '../axios-base';
import {tokenStorage} from '../auth/tokenStorage';

export interface ProjectGalleryItem {
    url: string;
    alt: string;
}

export interface JuryProject {
    _id: string;
    contestantName: string;
    projectTitle?: string;
    projectCategory?: string;
    projectGallery?: ProjectGalleryItem[];
    voteCount?: number;
    isFinalist?: boolean;
}

export interface PollEntry {
    _id: string;
    project: string | { _id: string };
    voteCount: number;
}

export interface JurorData {
    poll: PollEntry[];
    favoriteProjects: (string | { _id: string })[];
}

interface JuryState {
    projects: JuryProject[];
    categoryOptions: { value: string; displayValue: string }[];
    votes: Record<string, number>;
    bookmarks: Set<string>;
    loading: boolean;
    hydrated: boolean;
    voteLimit: number;
    voteLimitPerProject: number;
}

interface JuryActions {
    setLimits: (voteLimit: number, voteLimitPerProject: number) => void;
    fetchData: () => Promise<void>;
    castVote: (projectId: string, delta: number) => Promise<void>;
    toggleBookmark: (projectId: string) => Promise<void>;
    reset: () => void;
}

type JuryStore = JuryState & JuryActions;

// Helpers
const authHeader = () => ({
    headers: {Authorization: `Bearer ${tokenStorage.getAccessToken()}`}
});

const projectId = (ref: string | { _id: string }): string =>
    typeof ref === 'string' ? ref : ref._id;

const initialState: JuryState = {
    projects: [],
    categoryOptions: [],
    votes: {},
    bookmarks: new Set(),
    loading: true,
    hydrated: false,
    voteLimit: 10,
    voteLimitPerProject: 10,
};

// Store

/**
 * Zustand store for jury session state.
 * Data is fetched once per session (guarded by `hydrated`) and then kept
 * in memory, so navigating between Teilnehmer / Merkliste / Abstimmung
 * never triggers a refetch.
 */
const useJuryStore = create<JuryStore>((set, get) => ({
    ...initialState,

    setLimits: (voteLimit, voteLimitPerProject) =>
        set({voteLimit, voteLimitPerProject}),

    fetchData: async () => {
        if (get().hydrated) return;

        try {
            const jurorId = "69d8aebe0df5cfc046f0af47";

            const requests: Promise<any>[] = [
                axios.get('/jury/projects'),
                axios.get('/options/'),
            ];
            if (jurorId) {
                requests.push(axios.get(`/jury/${jurorId}`, authHeader()));
            }

            const [projectsRes, optionsRes, jurorRes] = await Promise.all(requests);

            const categoryOptions: JuryState['categoryOptions'] =
                optionsRes.data[0]?.projectForm?.projectCategory?.elementConfig?.options ?? [];

            const votes: Record<string, number> = {};
            const bookmarks = new Set<string>();

            if (jurorRes) {
                const juror: JurorData = jurorRes.data;
                juror.poll.forEach(item => {
                    votes[projectId(item.project)] = item.voteCount;
                });
                juror.favoriteProjects.forEach(p => {
                    bookmarks.add(projectId(p));
                });
            }

            set({
                projects: projectsRes.data,
                categoryOptions,
                votes,
                bookmarks,
                loading: false,
                hydrated: true,
            });
        } catch (err) {
            console.error('Error fetching jury data:', err);
            set({loading: false, hydrated: true});
        }
    },

    castVote: async (projectId, delta) => {
        const {votes, voteLimit, voteLimitPerProject} = get();
        const current = votes[projectId] ?? 0;
        const next = current + delta;

        if (next < 0) return;
        if (delta > 0 && next > voteLimitPerProject) return;
        if (delta > 0) {
            const used = Object.values(votes).reduce((s, v) => s + v, 0);
            if (used + delta > voteLimit) return;
        }

        set({votes: {...votes, [projectId]: next}});

        const jurorId = "69d8aebe0df5cfc046f0af47";
        if (!jurorId) return;

        try {
            if (delta > 0) {
                await axios.post(`/jury/${jurorId}/vote`, {projectId}, authHeader());
            } else {
                await axios.delete(`/jury/${jurorId}/vote`, {...authHeader(), data: {projectId}});
            }
        } catch (err) {
            console.error('Vote sync failed:', err);
            set({votes: {...get().votes, [projectId]: current}});
        }
    },

    toggleBookmark: async (projectId) => {
        const {bookmarks} = get();
        const wasBookmarked = bookmarks.has(projectId);

        const next = new Set(bookmarks);
        if (wasBookmarked) next.delete(projectId);
        else next.add(projectId);
        set({bookmarks: next});

        const jurorId = "69d8aebe0df5cfc046f0af47";
        if (!jurorId) return;

        try {
            if (wasBookmarked) {
                await axios.delete(`/jury/${jurorId}/favorite`, {
                    ...authHeader(), data: {favoriteProject: projectId}
                });
            } else {
                await axios.post(`/jury/${jurorId}/favorite`, {favoriteProject: projectId}, authHeader());
            }
        } catch (err) {
            console.error('Bookmark sync failed:', err);
            const rolled = new Set(get().bookmarks);
            if (wasBookmarked) rolled.add(projectId);
            else rolled.delete(projectId);
            set({bookmarks: rolled});
        }
    },

    // Call this on logout
    reset: () => set({...initialState, bookmarks: new Set()}),
}));

export default useJuryStore;
